import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import minddeckLogo from "../assets/minddeck/logo.png";
import jsonLogo from "../assets/jsonparser/logo.png";
import { FiArrowRight, FiZap, FiShield, FiGlobe } from "react-icons/fi";
import { useLanguage } from "../contexts/LanguageContext";

const Home = () => {
    const { t, language } = useLanguage();

    // 번역 테스트를 위한 콘솔 출력
    console.log("Current language:", language);
    console.log("Translated title:", t("home.title"));
    console.log("Translated subtitle:", t("home.subtitle"));

    const tools = [
        {
            title: t("home.minddeck.title"),
            description: t("home.minddeck.desc"),
            href: "/tools/minddeck",
            logo: minddeckLogo,
            bgWhite: true,
            gradient: "from-purple-500 to-pink-500",
            bgGradient:
                "from-purple-50 to-pink-50 dark:from-purple-900/20 to-pink-900/20",
        },
        {
            title: t("home.jsonparser.title"),
            description: t("home.jsonparser.desc"),
            href: "/tools/json-parser",
            logo: jsonLogo,
            bgWhite: false,
            gradient: "from-blue-500 to-cyan-500",
            bgGradient:
                "from-blue-50 to-cyan-50 dark:from-blue-900/20 to-cyan-900/20",
        },
    ];

    const features = [
        {
            icon: FiZap,
            title: t("home.features.performance.title"),
            description: t("home.features.performance.desc"),
        },
        {
            icon: FiShield,
            title: t("home.features.security.title"),
            description: t("home.features.security.desc"),
        },
        {
            icon: FiGlobe,
            title: t("home.features.multilingual.title"),
            description: t("home.features.multilingual.desc"),
        },
    ];

    return (
        <div className="py-12 space-y-20">
            {/* Hero Section */}
            <section className="text-center space-y-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 bg-clip-text text-transparent mb-6">
                        {t("home.title")}
                    </h1>
                    <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
                        {t("home.subtitle")}
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <Link
                        to="/tools"
                        className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300"
                    >
                        <span>{t("home.tools.title")}</span>
                        <FiArrowRight className="w-5 h-5" />
                    </Link>
                </motion.div>
            </section>

            {/* Tools Section */}
            <section className="space-y-8">
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="text-3xl font-bold text-center"
                >
                    {t("home.tools.title")}
                </motion.h2>

                <div className="grid md:grid-cols-2 gap-8">
                    {tools.map((tool, index) => (
                        <motion.div
                            key={tool.href}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            whileHover={{ y: -10 }}
                            className="group"
                        >
                            <Link
                                to={tool.href}
                                className={`block p-8 rounded-2xl bg-gradient-to-br ${tool.bgGradient} border border-gray-200 dark:border-gray-700 hover:border-transparent hover:shadow-2xl transition-all duration-300`}
                            >
                                <div className="flex items-center space-x-4 mb-4">
                                    <div
                                        className={`p-3 rounded-xl ${
                                            tool.bgWhite
                                                ? "bg-white"
                                                : `bg-gradient-to-r ${tool.gradient} text-white`
                                        }`}
                                    >
                                        <img
                                            src={tool.logo}
                                            alt={tool.title + " 로고"}
                                            className="w-8 h-8"
                                        />
                                    </div>
                                    <h3 className="text-xl font-semibold">
                                        {tool.title}
                                    </h3>
                                </div>
                                <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                                    {tool.description}
                                </p>
                                <div className="flex items-center text-sm font-medium text-purple-600 dark:text-purple-400 group-hover:text-purple-700 dark:group-hover:text-purple-300">
                                    <span>{t("home.learnMore")}</span>
                                    <FiArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Features Section */}
            <section className="space-y-8">
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="text-3xl font-bold text-center"
                >
                    {t("home.features.title")}
                </motion.h2>

                <div className="grid md:grid-cols-3 gap-8">
                    {features.map((feature, index) => (
                        <motion.div
                            key={feature.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            className="text-center p-6"
                        >
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-2xl mb-4">
                                <feature.icon className="w-8 h-8" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2">
                                {feature.title}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">
                                {feature.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* CTA Section */}
            <section className="text-center space-y-8 py-16 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-3xl">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                >
                    <h2 className="text-3xl font-bold mb-4">
                        {t("home.cta.title")}
                    </h2>
                    <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
                        {t("home.cta.subtitle")}
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                        <Link
                            to="/tools/minddeck"
                            className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300"
                        >
                            {t("home.cta.minddeck")}
                        </Link>
                        <Link
                            to="/tools/json-parser"
                            className="px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300"
                        >
                            {t("home.cta.jsonparser")}
                        </Link>
                    </div>
                </motion.div>
            </section>
        </div>
    );
};

export default Home;
